import React, { forwardRef, useEffect, useRef, useState } from "react";
import "./login.style.scss";

import { useLocation, useNavigate } from "react-router-dom";

import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { FaLock, FaRegUser, FaRegEye, FaRegEyeSlash } from "react-icons/fa";

import logoDark from "../../assets/logo-dark-BILLME.png";
import Spinner from "../../components/spinner/spinner.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";
import FormError from "../../components/form-error/form-error.component";
import SuccessDisplay from "../../components/success-display/success-display.component";
import {
	readAndClearAuthBanner,
	type AuthBannerPayload,
} from "../../global/query-client";
import {
	useForgotPassword,
	useLogin,
	useResetPassword,
} from "../../queries/auth.queries";

const loginSchema = yup.object().shape({
	username: yup.string().required("Username cannot be empty"),
	password: yup
		.string()
		.required("Password cannot be empty")
		.min(8, "Password must be atleast 8 letters"),
});

const forgotPasswordSchema = yup.object().shape({
	username: yup.string().required("Username cannot be empty"),
});

const resetPasswordSchema = yup.object().shape({
	password: yup
		.string()
		.required("Password cannot be empty")
		.min(8, "Password must be atleast 8 letters"),
	confirm_password: yup
		.string()
		.oneOf([yup.ref("password"), null], "Passwords must match")
		.required("Password cannot be empty"),
});

export const PasswordInputField = forwardRef<
	HTMLInputElement,
	React.InputHTMLAttributes<HTMLInputElement> & {
		label?: string;
		error?: string;
	}
>((props, ref) => {
	const [pswrdVisible, setPswrdVisible] = useState(false);
	return (
		<div className="input-wrapper">
			{props.label && <p className="input-label">{props.label}</p>}
			<div className="input-wrapper-inner">
				<FaLock />
				<input
					{...props}
					ref={ref}
					type={pswrdVisible ? "text" : "password"}
					className="input-container"
				/>
				<div
					className="input-action"
					onClick={() => setPswrdVisible(!pswrdVisible)}
				>
					{pswrdVisible ? <FaRegEyeSlash /> : <FaRegEye />}
				</div>
			</div>
			<span className="form-validation-error">{props.error}</span>
		</div>
	);
});

export default function Login() {
	const loginMutation = useLogin();
	const forgotPasswordMutation = useForgotPassword();
	const resetPasswordMutation = useResetPassword();

	const [forgotPswrd, setForgotPswrd] = useState(false);
	const [selectCompany, setSelectCompany] = useState<{
		display: boolean;
		companyList: unknown[];
		credentials: { username: string; password: string };
	}>({
		display: false,
		companyList: [],
		credentials: { username: "", password: "" },
	});

	const navigate = useNavigate();
	const location = useLocation();

	// Read once on arrival — set by the global axios 403 interceptor
	// (session-expired) or by useLogout/useUpdatePassword (manual logout,
	// forced logout after a password change) right before clearing the
	// session, so it's only ever present after one of those, never on a
	// plain unauthenticated visit to /login.
	const [authBanner, setAuthBannerState] = useState<AuthBannerPayload | null>(
		null,
	);
	useEffect(() => {
		setAuthBannerState(readAndClearAuthBanner());
	}, []);

	// Brief SuccessDisplay before navigating away — same ~1.2s pattern used
	// elsewhere for a mutation that finishes by leaving the current view.
	const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	useEffect(() => {
		return () => {
			if (navigateTimeoutRef.current)
				clearTimeout(navigateTimeoutRef.current);
		};
	}, []);

	// BE emails a single `?token=` param (see billing_system_BE
	// middleware/sendGridEmailHelper.js) — presence of the token is what
	// puts this page in reset mode, on either /login or /reset-password.
	const verificationToken =
		new URLSearchParams(location.search).get("token") ?? undefined;
	const resetPasswordParam = Boolean(verificationToken);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<{
		username?: string;
		password?: string;
		confirm_password?: string;
	}>({
		defaultValues: {},
		mode: "onSubmit",
		// Three mutually exclusive schemas share one form instance — cast avoids
		// TS complaining about the resolver union's incompatible value shapes.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: (resetPasswordParam
			? yupResolver(resetPasswordSchema)
			: forgotPswrd
				? yupResolver(forgotPasswordSchema)
				: yupResolver(loginSchema)) as any,
	});

	const isPending =
		loginMutation.isPending ||
		forgotPasswordMutation.isPending ||
		resetPasswordMutation.isPending;

	const handleLogin = (loginData: {
		username: string;
		password: string;
		company_code?: string;
	}) => {
		if (isPending) return;
		loginMutation.mutate(loginData, {
			onSuccess: (result) => {
				if (!result.status && result.data) {
					setSelectCompany({
						display: true,
						companyList: result.data as unknown[],
						credentials: loginData,
					});
					return;
				}
				// errorStatus === 100 (company not yet IRD-verified) needs no
				// message here — ProtectedRoute redirects to /verification on
				// its own once it reads company_verified: false from the
				// session it's about to fetch, and that page already explains
				// the situation persistently.
				navigate("/");
			},
		});
	};

	const handleForgotPassword = (forgotPasswordData: {
		username: string;
	}) => {
		if (isPending) return;
		forgotPasswordMutation.mutate(forgotPasswordData);
	};

	const handleResetPassword = (resetPasswordData: {
		password: string;
		confirm_password: string;
	}) => {
		if (isPending) return;
		resetPasswordMutation.mutate(
			{ ...resetPasswordData, verificationToken: verificationToken! },
			{
				onSuccess: () => {
					reset();
					navigateTimeoutRef.current = setTimeout(
						() => navigate("/login"),
						1200,
					);
				},
			},
		);
	};

	function _renderForgotPassword() {
		return (
			<div className="form-container">
				<h2 className="title-text">Forgot Password</h2>

				<p className="description-text">
					Enter your username and we will send a password reset
					link to the email on file.
				</p>

				<FormError
					isError={forgotPasswordMutation.isError}
					error={forgotPasswordMutation.error}
					fallback="Failed to send reset link."
				/>
				<InlineNotice
					message={
						forgotPasswordMutation.isSuccess
							? (forgotPasswordMutation.data ?? null)
							: null
					}
					type="success"
					onDismiss={() => forgotPasswordMutation.reset()}
				/>

				<div className="form-inputs-container">
					<div className="input-wrapper">
						<div className="input-wrapper-inner">
							<FaRegUser />
							<input
								{...register("username")}
								type="text"
								className="input-container"
								placeholder="Username"
							/>
						</div>
						<span className="form-validation-error">
							{errors?.username?.message}
						</span>
					</div>
				</div>

				<div
					className="form-change-btn"
					onClick={() => setForgotPswrd(false)}
				>
					Back to login?
				</div>
				<div
					className="form-submit-btn"
					onClick={handleSubmit(handleForgotPassword)}
				>
					{isPending && <Spinner styles={undefined} />}
					Send Reset Link
				</div>
			</div>
		);
	}

	function _renderResetPassword() {
		if (resetPasswordMutation.isSuccess) {
			return (
				<div className="form-container">
					<SuccessDisplay
						title="Password reset"
						description="Redirecting you to sign in..."
					/>
				</div>
			);
		}

		return (
			<div className="form-container">
				<h2 className="title-text">Reset Password</h2>

				<p className="description-text">
					Please enter and confirm your new password to access your
					account.
				</p>

				<FormError
					isError={resetPasswordMutation.isError}
					error={resetPasswordMutation.error}
					fallback="Failed to reset password."
				/>

				<div className="form-inputs-container">
					<PasswordInputField
						{...register("password")}
						placeholder="Password"
						error={errors?.password?.message}
					/>
					<PasswordInputField
						{...register("confirm_password")}
						placeholder="Confirm Password"
						error={errors?.confirm_password?.message}
					/>
				</div>

				<div
					className="form-submit-btn"
					onClick={handleSubmit(handleResetPassword)}
				>
					{isPending && <Spinner styles={undefined} />}
					Change Password
				</div>
			</div>
		);
	}

	function _renderLogin() {
		return (
			<div className="form-container">
				<h2 className="title-text">Sign in</h2>
				<p className="description-text">use your username and password</p>

				<FormError
					isError={loginMutation.isError}
					error={loginMutation.error}
					fallback="Failed to sign in."
				/>

				<div className="form-inputs-container">
					<div className="input-wrapper">
						<div className="input-wrapper-inner">
							<FaRegUser />
							<input
								{...register("username")}
								type="text"
								className="input-container"
								placeholder="Username"
							/>
						</div>
						<span className="form-validation-error">
							{errors?.username?.message}
						</span>
					</div>
					<PasswordInputField
						{...register("password")}
						placeholder="Password"
						error={errors?.password?.message}
					/>
				</div>

				<div
					className="form-change-btn"
					onClick={() => setForgotPswrd(true)}
				>
					Forgot your password?
				</div>
				<div
					className="form-submit-btn"
					onClick={handleSubmit(handleLogin)}
				>
					{isPending ? (
						<>
							<Spinner styles={undefined} />
							<span>Signing in...</span>
						</>
					) : (
						"Sign in"
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="login-wrapper">
			<div className="login-inner">
				<div className="login-page-header">
					<img
						src={logoDark}
						alt="BillMe"
						className="login-page-header-logo"
					/>
					<h1>BillMe</h1>
				</div>

				{authBanner && (
					<InlineNotice
						message={authBanner.message}
						type={authBanner.type}
						onDismiss={() => setAuthBannerState(null)}
					/>
				)}

				<div className="login-container">
					{!selectCompany.display ? (
						<div className="login-container-inner">
							{resetPasswordParam
								? _renderResetPassword()
								: forgotPswrd
									? _renderForgotPassword()
									: _renderLogin()}
							<div className="description-container">
								<h2 className="title-text inverted">
									Welcome Back!
								</h2>
								<p className="description-text">
									Enter your account details and start
									generating bills.
								</p>
							</div>
						</div>
					) : (
						<div className="login-company-select">
							<div className="title-text">Select Company</div>
							<div className="description-text">
								Select the company you want to log into
							</div>
							<div className="company-list-container">
								{selectCompany.companyList.length > 0 &&
									(selectCompany.companyList as any[]).map(
										(company, key) => (
											<div
												key={key}
												className="company-list cap-display"
												onClick={() => {
													handleLogin({
														...selectCompany.credentials,
														company_code:
															company.company_code,
													});
												}}
											>
												<div className="company-logo">
													<img
														crossOrigin="anonymous"
														src={
															company.company_logo
														}
														alt="Company Logo"
													/>
												</div>
												<div>
													{company?.company_name}
												</div>
											</div>
										),
									)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
